import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Phone, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COMPANY, NAV_LINKS } from "@/lib/constants";
import { useLeadDialog } from "@/components/LeadForms";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { openQuote, openSurvey } = useLeadDialog();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass border-b border-slate-800/80 py-2.5" : "bg-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex items-center justify-between">
        <a href="#top" data-testid="header-logo" className="flex items-center gap-3 group">
          <img src={COMPANY.logo} alt="Atlas Security & Life Safety" className="h-11 w-11 object-contain" />
          <div className="leading-tight">
            <div className="font-display font-extrabold tracking-wider text-lg metallic-text">ATLAS</div>
            <div className="text-[9px] tracking-[0.22em] text-[#38BDF8] font-semibold">SECURITY & LIFE SAFETY</div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-slate-300 hover:text-white transition-colors relative after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-0 after:bg-[#38BDF8] hover:after:w-full after:transition-all"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a href={COMPANY.phoneHref} className="flex items-center gap-2 text-sm text-slate-300 hover:text-[#38BDF8] transition-colors" data-testid="header-phone-link">
            <Phone className="h-4 w-4" /> {COMPANY.phone}
          </a>
          <Button data-testid="header-nav-survey-btn" onClick={openSurvey} variant="outline" className="border-slate-600 bg-transparent text-slate-100 hover:bg-slate-800 hover:text-white">
            Book Survey
          </Button>
          <Button data-testid="header-nav-quote-btn" onClick={openQuote} className="bg-[#0066FF] hover:bg-[#0052cc] text-white font-semibold glow-blue">
            Request Quote
          </Button>
        </div>

        <button className="lg:hidden text-slate-200" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden glass border-t border-slate-800 mt-2.5 px-6 py-5 space-y-4">
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="block text-slate-200 font-medium">
              {l.label}
            </a>
          ))}
          <a href={COMPANY.phoneHref} className="flex items-center gap-2 text-[#38BDF8]">
            <Phone className="h-4 w-4" /> {COMPANY.phone}
          </a>
          <div className="flex flex-col gap-2 pt-2">
            <Button onClick={() => { openSurvey(); setOpen(false); }} variant="outline" className="border-slate-600 bg-transparent text-slate-100 w-full">Book Survey</Button>
            <Button onClick={() => { openQuote(); setOpen(false); }} className="bg-[#0066FF] text-white w-full">Request Quote</Button>
          </div>
          <Link to="/owner" onClick={() => setOpen(false)} className="flex items-center gap-2 text-xs text-slate-500 pt-1">
            <ShieldCheck className="h-3.5 w-3.5" /> Owner Portal
          </Link>
        </div>
      )}
    </header>
  );
};
